import { Capacitor, registerPlugin } from '@capacitor/core';

const PLUGIN_NAME = 'KioskEnrollment';
const ENROLLMENT_PATH = '/v1/stores/kiosk/enroll';
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{40,100}$/;
const API_BASE = import.meta.env.VITE_API_BASE || 'https://dev.api.ampersandbrands.com';

interface KioskEnrollmentPlugin {
  enroll(options: { token: string }): Promise<void>;
  enrollFromManagedConfiguration(): Promise<ManagedEnrollmentResult>;
}

export type ManagedEnrollmentMode = 'kiosk' | 'employee';

export type ManagedEnrollmentStatus =
  | 'started'
  | 'missing'
  | 'invalid'
  | 'already_attempted'
  | 'not_applicable';

export type ManagedEnrollmentResult = {
  status: ManagedEnrollmentStatus;
  mode: ManagedEnrollmentMode;
};

const KioskEnrollment = registerPlugin<KioskEnrollmentPlugin>(PLUGIN_NAME);

export function canUseNativeKioskEnrollment(): boolean {
  return (
    Capacitor.getPlatform() === 'android'
    && Capacitor.isNativePlatform()
    && Capacitor.isPluginAvailable(PLUGIN_NAME)
  );
}

export function tokenFromEnrollmentUrl(value: string): string | null {
  try {
    const url = new URL(value.trim());
    const apiUrl = new URL(API_BASE);
    const token = url.searchParams.get('token');
    const hasOnlyToken = [...url.searchParams.keys()].every((key) => key === 'token');

    if (
      url.protocol !== 'https:'
      || url.hostname !== apiUrl.hostname
      || url.port
      || url.username
      || url.password
      || url.pathname !== ENROLLMENT_PATH
      || !hasOnlyToken
      || !token
      || !TOKEN_PATTERN.test(token)
    ) {
      return null;
    }

    return token;
  } catch {
    return null;
  }
}

export async function enrollNativeKiosk(token: string): Promise<void> {
  if (!TOKEN_PATTERN.test(token)) {
    throw new Error('Invalid kiosk enrollment token');
  }

  await KioskEnrollment.enroll({ token });
}

export async function enrollNativeKioskFromManagedConfiguration(): Promise<ManagedEnrollmentResult> {
  return KioskEnrollment.enrollFromManagedConfiguration();
}
